import { useEffect, useRef, useState } from 'react'

import { reportRpcError } from '../services/report-rpc-error'

export type PendingApprovalsCountInput = {
  /** RPC invoker from `useOrgGatewayRpc`. */
  call: <T = unknown>(method: string, params?: unknown) => Promise<T>
  /** Active organization id, used for error reporting context. */
  organizationId: string | null
  /** When false, polling is paused (e.g. gateway disconnected). */
  enabled: boolean
  /** Poll interval in ms. Defaults to NFR-002 budget (5000ms). */
  intervalMs?: number
}

type PendingApprovalsResult = {
  pending?: unknown[] | { items?: unknown[] }
  count?: number
}

function extractCount(result: unknown): number {
  if (!result || typeof result !== 'object') return 0
  const r = result as PendingApprovalsResult
  if (typeof r.count === 'number') return r.count
  if (Array.isArray(r.pending)) return r.pending.length
  if (r.pending && typeof r.pending === 'object' && Array.isArray((r.pending as { items?: unknown[] }).items)) {
    return ((r.pending as { items?: unknown[] }).items ?? []).length
  }
  return 0
}

/**
 * FR-022 / T008 / T022: single polling data source for the pending-approvals
 * indicator. Shared by `PendingApprovalsBadge` and (later) `ApprovalsPage`.
 *
 * Polls `exec.approvals.list` at the NFR-002 budget (one request per 5s). Skips
 * when the tab is hidden so the budget is not wasted.
 */
export function usePendingApprovalsCount({
  call,
  organizationId,
  enabled,
  intervalMs = 5000,
}: PendingApprovalsCountInput): { count: number; lastError: string | null } {
  const [count, setCount] = useState(0)
  const [lastError, setLastError] = useState<string | null>(null)
  const activeRef = useRef(true)

  useEffect(() => {
    activeRef.current = true
    return () => { activeRef.current = false }
  }, [])

  useEffect(() => {
    if (!enabled) {
      setCount(0)
      return
    }

    let timer: ReturnType<typeof setTimeout> | null = null
    let inFlight = false

    const tick = async () => {
      if (!activeRef.current) return
      if (typeof document !== 'undefined' && document.hidden) {
        schedule()
        return
      }
      if (inFlight) {
        schedule()
        return
      }
      inFlight = true
      try {
        const result = await call('exec.approvals.list', {})
        if (!activeRef.current) return
        setCount(extractCount(result))
        setLastError(null)
      } catch (err) {
        if (!activeRef.current) return
        setLastError(err instanceof Error ? err.message : String(err))
        reportRpcError(err, {
          method: 'exec.approvals.list',
          params: {},
          organizationId,
        })
      } finally {
        inFlight = false
        schedule()
      }
    }

    const schedule = () => {
      if (!activeRef.current) return
      if (timer !== null) clearTimeout(timer)
      timer = setTimeout(tick, intervalMs)
    }

    void tick()

    return () => {
      if (timer !== null) clearTimeout(timer)
    }
  }, [call, enabled, intervalMs, organizationId])

  return { count, lastError }
}
